type BuildFullPathProjectionParams = {
  fieldName?: string;
  maxDepth?: number;
};

const DEFAULT_MAX_DEPTH = 7;

/**
 * Builds a GROQ projection that resolves a route node's full path from parent slugs.
 */
function buildFullPathProjection({
  fieldName = "fullPath",
  maxDepth = DEFAULT_MAX_DEPTH,
}: BuildFullPathProjectionParams = {}): string {
  const parentChain = Array.from({ length: maxDepth }, (_, index) => {
    const repeats = maxDepth - index - 1;
    const parentPath = repeats > 0 ? `${"parent->".repeat(repeats)}` : "";
    return `${parentPath}slug.current`;
  });

  return `"${fieldName}": select(
    nodeRole == "site-root" => "/",
    true => "/" + array::join(
      array::compact([
        ${parentChain.join(",\n        ")}
      ]),
      "/"
    ) + "/"
  )`;
}

/**
 * Static expansion of `buildFullPathProjection()` (with default depth) for use
 * in typegen-only sibling queries.
 *
 * Sanity TypeGen statically inlines values referenced inside `groq` template
 * literals and cannot evaluate function bodies (it errors with
 * "Unsupported expression type: BlockStatement"). Runtime queries should keep
 * calling `buildFullPathProjection()` so depth stays maintainable; mirror them
 * in a separate static query that uses this constant for type generation.
 */
const staticFullPathProjection = `"fullPath": select(
    nodeRole == "site-root" => "/",
    true => "/" + array::join(
      array::compact([
        parent->parent->parent->parent->parent->parent->slug.current,
        parent->parent->parent->parent->parent->slug.current,
        parent->parent->parent->parent->slug.current,
        parent->parent->parent->slug.current,
        parent->parent->slug.current,
        parent->slug.current,
        slug.current
      ]),
      "/"
    ) + "/"
  )`;

export { buildFullPathProjection, staticFullPathProjection };
